import {
  normalizeCapabilityNavigationTarget,
  type CapabilityNavigationTarget,
} from "../config/capabilityNavigation";
import { ORCA_ELICITATION_SCHEMA } from "./orcaElicitation";

export const ORCA_GUIDANCE_SCHEMA = "orca.guidance/v1" as const;
export const ORCA_GUIDANCE_MAX_TEXT_LENGTH = 120;
export const ORCA_RAIL_READING_MAX_TEXT_LENGTH = 360;
export const ORCA_GUIDANCE_MAX_ATTENTION_ITEMS = 12;

export type OrcaGuidanceReadingSource = "cortex" | "rules_language" | "rules_fallback";
export type OrcaRailReadingStatus = "ready" | "unavailable";
export type OrcaCortexFailureCode =
  | "runtime_unavailable"
  | "invalid_response"
  | "invalid_selection"
  | "invalid_rail_reading";

export interface OrcaPrimaryReadingV1 {
  text: string;
  source: "rules_language";
}

export interface OrcaRailReadingV1 {
  status: OrcaRailReadingStatus;
  source: "cortex" | "rules_fallback";
  text?: string;
  failureCode?: OrcaCortexFailureCode;
}

export interface OrcaGuidanceAttentionItem {
  id: string;
  kind: OrcaGuidanceV1["kind"];
  text: string;
  priority: OrcaGuidanceV1["priority"];
  actionLabel: string;
  target?: CapabilityNavigationTarget;
  elicitation?: OrcaElicitationReferenceV1;
}

export interface OrcaElicitationReferenceV1 {
  schema: typeof ORCA_ELICITATION_SCHEMA;
  requestId: string;
}

export interface OrcaGuidanceV1 {
  schema: typeof ORCA_GUIDANCE_SCHEMA;
  scope: "project" | "screen";
  kind:
    | "conflict_question"
    | "state_change"
    | "approval"
    | "next_milestone"
    | "report_status"
    | "screen_insight"
    | "fallback";
  text: string;
  priority: "urgent" | "action" | "info";
  target?: CapabilityNavigationTarget;
  contextVersion?: number;
  sourceRevision?: string;
  source: OrcaGuidanceReadingSource;
  /**
   * New project Directives carry a stable Home reading and a separate live
   * rail interpretation. Both fields are absent on legacy persisted records.
   */
  primaryReading?: OrcaPrimaryReadingV1;
  railReading?: OrcaRailReadingV1;
  attentionItems?: OrcaGuidanceAttentionItem[];
  elicitation?: OrcaElicitationReferenceV1;
}

const GUIDANCE_KINDS = new Set<OrcaGuidanceV1["kind"]>([
  "conflict_question",
  "state_change",
  "approval",
  "next_milestone",
  "report_status",
  "screen_insight",
  "fallback",
]);
const GUIDANCE_PRIORITIES = new Set<OrcaGuidanceV1["priority"]>([
  "urgent",
  "action",
  "info",
]);
const GUIDANCE_SOURCES = new Set<OrcaGuidanceV1["source"]>([
  "cortex",
  "rules_language",
  "rules_fallback",
]);
const CORTEX_FAILURE_CODES = new Set<OrcaCortexFailureCode>([
  "runtime_unavailable",
  "invalid_response",
  "invalid_selection",
  "invalid_rail_reading",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function optionalString(value: unknown, maxLength: number): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized && normalized.length <= maxLength ? normalized : undefined;
}

function parseElicitationReference(value: unknown): OrcaElicitationReferenceV1 | undefined {
  if (!isRecord(value) || value.schema !== ORCA_ELICITATION_SCHEMA) return undefined;
  const requestId = optionalString(value.requestId, 160);
  return requestId ? { schema: ORCA_ELICITATION_SCHEMA, requestId } : undefined;
}

function parseAttentionItem(value: unknown): OrcaGuidanceAttentionItem | null {
  if (!isRecord(value)) return null;

  const id = optionalString(value.id, 240);
  const text = optionalString(value.text, ORCA_GUIDANCE_MAX_TEXT_LENGTH);
  const actionLabel = optionalString(value.actionLabel, 52);
  if (!id || !text || !/[.!?]$/.test(text) || !actionLabel) return null;
  if (!GUIDANCE_KINDS.has(value.kind as OrcaGuidanceV1["kind"])) return null;
  if (!GUIDANCE_PRIORITIES.has(value.priority as OrcaGuidanceV1["priority"])) return null;

  const target = value.target === undefined
    ? undefined
    : normalizeCapabilityNavigationTarget(value.target);
  if (value.target !== undefined && !target) return null;
  const elicitation = parseElicitationReference(value.elicitation);
  if (value.elicitation !== undefined && !elicitation) return null;

  return {
    id,
    kind: value.kind as OrcaGuidanceV1["kind"],
    text,
    priority: value.priority as OrcaGuidanceV1["priority"],
    actionLabel,
    ...(target ? { target } : {}),
    ...(elicitation ? { elicitation } : {}),
  };
}

function parseAttentionItems(value: unknown): OrcaGuidanceAttentionItem[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const seen = new Set<string>();
  const items: OrcaGuidanceAttentionItem[] = [];
  for (const rawItem of value) {
    const item = parseAttentionItem(rawItem);
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    items.push(item);
    if (items.length === ORCA_GUIDANCE_MAX_ATTENTION_ITEMS) break;
  }
  return items;
}

function parsePrimaryReading(value: unknown): OrcaPrimaryReadingV1 | null {
  if (!isRecord(value) || value.source !== "rules_language") return null;
  const text = optionalString(value.text, ORCA_GUIDANCE_MAX_TEXT_LENGTH);
  return text && /[.!?]$/.test(text)
    ? { text, source: "rules_language" }
    : null;
}

function parseRailReading(value: unknown): OrcaRailReadingV1 | null {
  if (!isRecord(value)) return null;

  if (value.status === "ready") {
    if (value.source !== "cortex" || value.failureCode !== undefined) return null;
    const text = optionalString(value.text, ORCA_RAIL_READING_MAX_TEXT_LENGTH);
    return text && /[.!?]$/.test(text)
      ? { status: "ready", source: "cortex", text }
      : null;
  }

  if (value.status !== "unavailable"
      || value.source !== "rules_fallback"
      || value.text !== undefined
      || !CORTEX_FAILURE_CODES.has(value.failureCode as OrcaCortexFailureCode)) {
    return null;
  }
  return {
    status: "unavailable",
    source: "rules_fallback",
    failureCode: value.failureCode as OrcaCortexFailureCode,
  };
}

export function parseOrcaGuidanceV1(value: unknown): OrcaGuidanceV1 | null {
  if (!isRecord(value) || value.schema !== ORCA_GUIDANCE_SCHEMA) return null;
  if (value.scope !== "project" && value.scope !== "screen") return null;
  if (!GUIDANCE_KINDS.has(value.kind as OrcaGuidanceV1["kind"])) return null;
  if (!GUIDANCE_PRIORITIES.has(value.priority as OrcaGuidanceV1["priority"])) return null;
  if (!GUIDANCE_SOURCES.has(value.source as OrcaGuidanceV1["source"])) return null;

  const text = optionalString(value.text, ORCA_GUIDANCE_MAX_TEXT_LENGTH);
  if (!text || !/[.!?]$/.test(text)) return null;

  const target = value.target === undefined
    ? undefined
    : normalizeCapabilityNavigationTarget(value.target);
  if (value.target !== undefined && !target) return null;

  const contextVersion = value.contextVersion === undefined
    ? undefined
    : Number.isSafeInteger(value.contextVersion) && Number(value.contextVersion) >= 0
      ? Number(value.contextVersion)
      : null;
  if (contextVersion === null) return null;

  const sourceRevision = optionalString(value.sourceRevision, 160);
  if (value.sourceRevision !== undefined && !sourceRevision) return null;

  const attentionItems = parseAttentionItems(value.attentionItems);
  const elicitation = parseElicitationReference(value.elicitation);
  if (value.elicitation !== undefined && !elicitation) return null;

  const hasPrimaryReading = value.primaryReading !== undefined;
  const hasRailReading = value.railReading !== undefined;
  if (hasPrimaryReading !== hasRailReading) return null;
  const primaryReading = hasPrimaryReading ? parsePrimaryReading(value.primaryReading) : undefined;
  const railReading = hasRailReading ? parseRailReading(value.railReading) : undefined;
  if ((hasPrimaryReading && !primaryReading) || (hasRailReading && !railReading)) return null;
  if (primaryReading && (primaryReading.text !== text || value.source !== primaryReading.source)) {
    return null;
  }

  return {
    schema: ORCA_GUIDANCE_SCHEMA,
    scope: value.scope,
    kind: value.kind as OrcaGuidanceV1["kind"],
    text,
    priority: value.priority as OrcaGuidanceV1["priority"],
    ...(target ? { target } : {}),
    ...(contextVersion !== undefined ? { contextVersion } : {}),
    ...(sourceRevision ? { sourceRevision } : {}),
    source: value.source as OrcaGuidanceV1["source"],
    ...(primaryReading ? { primaryReading } : {}),
    ...(railReading ? { railReading } : {}),
    ...(attentionItems !== undefined ? { attentionItems } : {}),
    ...(elicitation ? { elicitation } : {}),
  };
}
